'use client';

import type { CouponGoodsItemDto } from '@musinsa/services-curator';
import { twMerge } from 'tailwind-merge';
import { ProductInformationPrimitive } from '../../common';
import { ProductShell } from '../../product/components/variants/ProductShell';
import { buildCouponGoodsItemGtmAttr } from '../utils/couponGoodsMusinsaAnalytics';

interface CouponGoodsCardProps {
  item: CouponGoodsItemDto;
  /** PLP 그리드 내 상품 순서 (0-based). GA4 view_item_list 의 item_list_index 로 사용. */
  index?: number;
  /** GA4 view_item_list / select_item 페이로드의 coupon 메타. isMusinsa 페이지에서만 주입. */
  coupon?: { couponId: number; couponName: string };
  /** 판매자 본인 여부. true 이면 판매수 노출, false(방문자) 이면 좋아요+별점+리뷰수 노출. */
  isOwner: boolean;
  /** PLP 상품 노출 시 호출 (impression_content). viewport 진입 시 1회. */
  onImpression?: (item: CouponGoodsItemDto) => void;
  /** PLP 상품 클릭 시 호출 (click_product / click_content). */
  onClickProduct?: (item: CouponGoodsItemDto) => void;
  className?: string;
}

/**
 * 쿠폰 적용 상품 페이지 PLP 의 단일 상품 카드.
 *
 * 정형 패턴 — `ProductShell`(이미지 + 좋아요 + 클릭 영역, ProductImageWrapper
 * 내장으로 좋아요 mutation 자체 처리) + `ProductInformationPrimitive.*`
 * (브랜드/상품명/할인가/판매수). `SearchedProduct.tsx` 와 동일 구조.
 */
export const CouponGoodsCard = ({
  item,
  index,
  coupon,
  isOwner,
  onImpression,
  onClickProduct,
  className,
}: CouponGoodsCardProps) => {
  const finalPrice = item.finalPrice ?? item.couponPrice ?? item.price;
  const finalDiscount = item.finalDiscount ?? item.couponSalesRate ?? item.salesRate ?? 0;
  const existsCoupon = item.existsCoupon === true;
  const isLowestPrice = item.isLowestPrice === true;

  const infoBlock = (
    <ProductInformationPrimitive.Container>
      <div>
        <ProductInformationPrimitive.BrandName brandName={item.brandName} />
        <ProductInformationPrimitive.GoodsName goodsName={item.goodsName} />
      </div>
      <ProductInformationPrimitive.DiscountPriceRow
        finalPrice={finalPrice}
        finalDiscount={finalDiscount}
        hasOptionPrice={item.hasOptionPrice}
      />
      {(existsCoupon || isLowestPrice) && (
        <ProductInformationPrimitive.ProductBadgeContainer>
          {existsCoupon && <ProductInformationPrimitive.CouponBadge />}
          {isLowestPrice && <ProductInformationPrimitive.LowestPriceBadge />}
        </ProductInformationPrimitive.ProductBadgeContainer>
      )}
      {isOwner ? (
        <ProductInformationPrimitive.SalesVolume totalQuantity={item.totalQuantity ?? 0} />
      ) : (
        <ProductInformationPrimitive.EngagementRow>
          <ProductInformationPrimitive.HeartLikeCount likeCount={item.likeCount ?? 0} />
          {item.reviewScore && (
            <ProductInformationPrimitive.ReviewScore reviewScore={item.reviewScore} />
          )}
          <ProductInformationPrimitive.ReviewCount reviewCount={item.reviewCount ?? 0} />
        </ProductInformationPrimitive.EngagementRow>
      )}
    </ProductInformationPrimitive.Container>
  );

  // GA4 view_item_list / select_item — getGtmAttr 결과의 className + data-* 속성을 함께
  // root element 에 박아야 GTM handler 가 attribute 를 read 할 수 있다.
  const gtm = coupon && index != null ? buildCouponGoodsItemGtmAttr(item, index, coupon) : null;
  const { className: gtmClassName, ...gtmDataAttrs } = gtm ?? { className: undefined };

  return (
    <ProductShell
      {...gtmDataAttrs}
      className={twMerge(className, gtmClassName)}
      item={item}
      infoBlock={infoBlock}
      enableDeeplink
      productUrl={item.linkUrl}
      callbackImpression={onImpression}
      callbackClick={onClickProduct}
    />
  );
};
